import React, { useState, useRef, useEffect } from 'react';
import { useFont } from '../../Design/FontContext';
import './MiniWidgetPreview.css';

// Import widget types for mini preview
import HeroHeader from '../WidgetTypes/HeroHeader';

// 🔒 PREVIEW MODE WRAPPER - Überschreibt responsive Hooks für feste Desktop-Darstellung
const PreviewModeWrapper = ({ children, data }) => {
    // Überschreibe alle responsive-relevanten Eigenschaften für feste Desktop-Darstellung
    const previewProps = {
        ...data,
        // 🔒 FORCE DESKTOP-EIGENSCHAFTEN - verhindert responsive Springen
        containerWidth: 1400,
        isMobileLayout: false,
        forceDesktopLayout: true,
        isPreviewMode: true,
        disableResponsive: true,
        // Simuliere große Container-Breite für alle Breakpoint-Checks
        offsetWidth: 1400,
        clientWidth: 1400,
        // Deaktiviere Resize-Listening
        disableResizeObserver: true,
        // 🔒 ZUSÄTZLICHE DESKTOP-FORCING FLAGS
        forcedContainerWidth: 1400,
        isDesktopForced: true,
        preventResponsiveChanges: true
    };

    // Klone das Child-Element und füge Preview-Props hinzu - mit fester Container-Breite
    return (
        <div 
            style={{ 
                width: '1400px', 
                height: 'auto',
                minWidth: '1400px',
                maxWidth: '1400px',
                position: 'relative',
                overflow: 'hidden'
            }}
            className="preview-mode-container"
        >
            {React.cloneElement(children, previewProps)}
        </div>
    );
};

/**
 * Mini Widget Preview Component
 * Renders widgets in a small, thumbnail-like container
 */
function MiniWidgetPreview({ widgetData, className = '', colors = {}, fonts = {} }) {
    const fontsFromContext = useFont();
    const [containerHeight, setContainerHeight] = useState(100); // ✅ LEAN: Niedriger Start-Wert, wird dynamisch angepasst
    const widgetRef = useRef(null);
    
    // ✅ Use fonts from props if provided, otherwise use FontContext
    const activeFonts = (fonts && Object.keys(fonts).length > 0) ? fonts : fontsFromContext;

    // 🎯 ULTRA-LEAN APPROACH: Use EXACT original data with minimal transformations
    // This ensures 100% accurate preview - what you see is what you get!
    const createMiniWidgetData = (data) => {
        return {
            ...data, // ✅ ALL original data - completely unmodified
            // Only add minimal desktop-forcing flags to prevent responsive breakpoints
            forceDesktopLayout: true,
            isMobileLayout: false,
            isPreviewMode: true,
            containerWidth: 1400,
            disableResponsive: true,
            isDesktopForced: true
            // ⚠️ NO margin changes, NO height changes, NO content modifications!
            // This ensures the preview is 100% identical to the live version
        };
    };

    // Render the appropriate widget in mini form
    const renderMiniWidget = () => {
        const widgetType = widgetData.type;
        const miniData = createMiniWidgetData(widgetData);

        // 🔒 DESKTOP-FORCED RENDERING - HeroHeader mit Original-Farben
        // ✅ COLORS PRIORITY: savedWithColors > colors > current brand colors
        // Templates haben savedWithColors, neue Widgets nutzen brand colors
        const widgetColors = widgetData.savedWithColors || widgetData.colors || colors;
        
        // console.log('🎨 [MINI PREVIEW] Rendering widget with colors:', {
        //     type: widgetType,
        //     hasSavedColors: !!widgetData.savedWithColors,
        //     hasWidgetColors: !!widgetData.colors,
        //     usingColors: widgetColors
        // });
        
        
        switch (widgetType) {
            case 'HeroHeader':
                return <PreviewModeWrapper data={miniData}><HeroHeader data={miniData} isEditMode={false} colors={widgetColors} /></PreviewModeWrapper>;
            default:
                console.warn('🔍 MiniWidgetPreview: Unknown widget type:', widgetType, 'Available data:', Object.keys(widgetData));
                return (
                    <div className="custom-template-widget-fallback">
                        <div className="custom-template-fallback-icon">
                            <i className="fas fa-puzzle-piece"></i>
                        </div>
                        <div className="custom-template-fallback-text">{widgetType}</div>
                        <div className="custom-template-fallback-subtext">Preview</div>
                    </div>
                );
        }
    };

    // Dynamic scaling and height calculation based on widget content
    useEffect(() => {
        if (widgetRef.current) {
            // Use a timeout to ensure the widget is fully rendered
            const measureAndScale = () => {
                const widgetElement = widgetRef.current;
                const containerElement = widgetElement?.parentElement;
                
                if (widgetElement && containerElement) {
                    // Reset any previous transforms to get accurate measurements
                    widgetElement.style.transform = 'none';
                    widgetElement.style.position = 'static';
                    
                    // Force a reflow to ensure accurate measurements
                    // eslint-disable-next-line no-unused-expressions
                    widgetElement.offsetHeight;
                    
                    // Get the actual dimensions - use multiple methods for accuracy
                    const actualWidth = Math.max(
                        widgetElement.scrollWidth,
                        widgetElement.offsetWidth,
                        widgetElement.getBoundingClientRect().width
                    );
                    const actualHeight = Math.max(
                        widgetElement.scrollHeight,
                        widgetElement.offsetHeight,
                        widgetElement.getBoundingClientRect().height
                    );
                    
                    // Target width: Always scale to 240px width
                    const targetWidth = 240;
                    
                    // Calculate proportional scale factor
                    // If widget is 1400px wide → scale = 240/1400 = 0.171
                    // If widget is 1690px wide → scale = 240/1690 = 0.142
                    const scaleFactor = targetWidth / actualWidth;
                    
                    // Calculate proportional height
                    // If widget is 1400px wide x 1690px high → scaled: 240px wide x 290px high
                    const proportionalHeight = actualHeight * scaleFactor;
                    
                    // Apply proportional scaling
                    widgetElement.style.transform = `scale(${scaleFactor})`;
                    widgetElement.style.transformOrigin = 'top left';
                    widgetElement.style.position = 'absolute';
                    widgetElement.style.top = '0px';
                    widgetElement.style.left = '0px';
                    
                    // Set container height to match proportional height with improved bounds
                    // ✅ LEAN: Keine min-height mehr - echte proportionale Höhe!
                    const clampedHeight = Math.min(proportionalHeight, 500); // Nur max-height für sehr große Widgets
                    
                    // Deaktivierte Logs für bessere Performance
                    // console.log('Widget proportional scaling:', {
                    //     originalSize: `${actualWidth}px × ${actualHeight}px`,
                    //     scaleFactor: scaleFactor.toFixed(3),
                    //     scaledSize: `240px × ${proportionalHeight.toFixed(1)}px`,
                    //     containerHeight: clampedHeight,
                    //     widgetType: widgetData.type
                    // });
                    
                    setContainerHeight(clampedHeight);
                }
            };

            // Optimized measurement with reduced frequency
            let measurementCount = 0;
            const maxMeasurements = 2; // Reduziert von 4 auf 2 Messungen
            
            const optimizedMeasure = () => {
                if (measurementCount >= maxMeasurements) return;
                measurementCount++;
                measureAndScale();
            };
            
            // Initial measurement
            optimizedMeasure();
            
            // Single delayed measurement for async content
            const timeoutId = setTimeout(optimizedMeasure, 200);
            
            // Debounced ResizeObserver to prevent excessive calls
            let resizeTimeout;
            const observer = new ResizeObserver(() => {
                if (resizeTimeout) clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    if (measurementCount < maxMeasurements) {
                        measureAndScale();
                    }
                }, 100); // Debounced to 100ms
            });
            
            observer.observe(widgetRef.current);
            
            return () => {
                clearTimeout(timeoutId);
                if (resizeTimeout) clearTimeout(resizeTimeout);
                observer.disconnect();
            };
        }
    }, [widgetData]);

    return (
        <div 
            className={`custom-template-mini-preview ${className}`}
            style={{ height: `${containerHeight}px` }}
        >
            <div className="custom-template-widget-renderer" ref={widgetRef}>
                {renderMiniWidget()}
            </div>
        </div>
    );
}

// 🚀 PERFORMANCE: Memoize MiniWidgetPreview - verhindert unnötige Re-Renders
// Rendert nur neu wenn sich widgetData, colors, fonts oder className wirklich ändern
export default React.memo(MiniWidgetPreview, (prevProps, nextProps) => {
    // Custom comparison function
    const widgetDataEqual = JSON.stringify(prevProps.widgetData) === JSON.stringify(nextProps.widgetData);
    const colorsEqual = JSON.stringify(prevProps.colors) === JSON.stringify(nextProps.colors);
    const fontsEqual = JSON.stringify(prevProps.fonts) === JSON.stringify(nextProps.fonts);
    const classNameEqual = prevProps.className === nextProps.className;
    
    const shouldSkipRender = widgetDataEqual && colorsEqual && fontsEqual && classNameEqual;
    
    if (shouldSkipRender) {
        // console.log('⏭️ [MINI PREVIEW] Skipping re-render - props unchanged');
    }
    
    return shouldSkipRender; // true = skip re-render, false = do re-render
});
